import { useEffect, useState } from 'react';
import { supabase } from './supabase';

// Where the login form stashes {email, role, backend, ...} on both demo and
// live logins. See src/pages/login/components/LoginForm.jsx.
const SESSION_KEY = 'userSession';

const ROLE_LABELS = {
  teacher: 'Special Education Teacher',
  admin: 'Administrator',
  paraprofessional: 'Paraprofessional',
  related_service: 'Related Service Provider',
};

export function labelForRole(role) {
  if (!role) return '';
  return ROLE_LABELS[role] || 'User';
}

function readLocalSession() {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(SESSION_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    if (!parsed.email) return null;
    return parsed;
  } catch {
    return null;
  }
}

// Turn "sarah.johnson@edutracker.edu" into "Sarah Johnson", or
// "s@example.com" into "S". Used as a fallback when no display name is on
// file.
export function deriveNameFromEmail(email) {
  if (!email || typeof email !== 'string') return '';
  const local = email.split('@')[0] || '';
  const parts = local
    .split(/[._+\-\s]/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase());
  return parts.join(' ');
}

export function initialsFor(nameOrEmail) {
  if (!nameOrEmail || typeof nameOrEmail !== 'string') return '?';
  const trimmed = nameOrEmail.trim();
  if (!trimmed) return '?';
  if (trimmed.includes('@')) return trimmed.charAt(0).toUpperCase();
  const words = trimmed.split(/\s+/).filter(Boolean);
  if (words.length === 0) return '?';
  return words
    .slice(0, 2)
    .map((w) => w.charAt(0).toUpperCase())
    .join('');
}

function firstNameOf(fullName) {
  if (!fullName) return '';
  const first = fullName.trim().split(/\s+/)[0] || '';
  return first;
}

// Merge whatever we can find into a single user object with a stable shape:
//   { name, firstName, email, role, roleLabel, userId, backend, initials }
function assembleUser(session, authUser) {
  const meta = authUser?.user_metadata || {};
  const email = authUser?.email || session?.email || '';
  const role = session?.role || meta.role || '';
  const nameFromMeta = meta.full_name || meta.name || meta.display_name;
  const name = (nameFromMeta || deriveNameFromEmail(email) || email || 'Signed-in user').trim();

  return {
    name,
    firstName: firstNameOf(name) || name,
    email,
    role,
    roleLabel: labelForRole(role),
    userId: authUser?.id || session?.userId || null,
    backend: authUser ? 'supabase' : session?.backend || 'demo',
    initials: initialsFor(name || email),
  };
}

// Hook: returns the current user in a shape safe for direct rendering,
// or null while we don't know yet. Kept dependency-free (no context) so it
// can be dropped into any component without a provider.
export function useCurrentUser() {
  const [user, setUser] = useState(() => {
    const session = readLocalSession();
    if (!session) return null;
    return assembleUser(session, null);
  });

  useEffect(() => {
    let cancelled = false;

    async function refresh() {
      const session = readLocalSession();
      let authUser = null;
      if (supabase) {
        try {
          const { data } = await supabase.auth.getUser();
          authUser = data?.user || null;
        } catch {
          authUser = null;
        }
      }
      if (cancelled) return;
      if (!session && !authUser) {
        setUser(null);
        return;
      }
      setUser(assembleUser(session, authUser));
    }

    refresh();

    let authSub = null;
    if (supabase) {
      try {
        const { data } = supabase.auth.onAuthStateChange(() => {
          refresh();
        });
        authSub = data?.subscription || null;
      } catch {
        authSub = null;
      }
    }

    const onStorage = (event) => {
      if (event.key === SESSION_KEY) refresh();
    };
    if (typeof window !== 'undefined') {
      window.addEventListener('storage', onStorage);
    }

    return () => {
      cancelled = true;
      try {
        authSub?.unsubscribe?.();
      } catch {
        // ignore
      }
      if (typeof window !== 'undefined') {
        window.removeEventListener('storage', onStorage);
      }
    };
  }, []);

  return user;
}
